import React, { useState, useEffect, useCallback } from 'react';
import { format, parseISO, getDay, addMonths, subMonths, differenceInCalendarDays, addDays } from 'date-fns';
import { getMonthlyRoster, updateShift, deleteShift, createShift, unpublishRosterVersion } from '../../services/rosterService';
import EditShiftModal from './EditShiftModal';
import apiClient from '../../services/api';
import { DndContext, DragEndEvent, DragOverlay, useDraggable, useDroppable, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ShiftTheme, getShiftStyle, getShiftAbbreviation, getLeaveStyle } from '../../utils/rosterStyles';
import { ConfirmDialog } from '../ui/ConfirmDialog';

interface Shift {
    id: string;
    userId: string;
    startTime: string;
    endTime: string;
    type: string;
    location?: string;
    needsCover?: boolean;
    coverReason?: string;
    coverSourceLeaveRequestId?: string;
    assignmentChanged?: boolean;
    assignmentChangeType?: string;
    assignmentSourceSwapId?: string;
    originalUserId?: string;
    originalUser?: {
        id: string;
        name: string;
        email: string;
        role?: { name: string };
    };
    _additionalShifts?: Shift[];
    user: {
        id: string;
        name: string;
        email: string;
        role: { name: string };
    };
}

interface ShiftAuditLog {
    id: string;
    action: string;
    createdAt: string;
    details?: Record<string, unknown> | string | null;
    user?: {
        id: string;
        name: string;
        email: string;
        role?: { name: string };
    } | null;
}

interface ShiftAuditData {
    shift: Shift;
    relatedShifts?: Shift[];
    auditLogs: ShiftAuditLog[];
}

interface StaffMember {
    id: string;
    name: string;
    role: string;
    shifts: Record<string, Shift>;
}

interface MonthlyRosterData {
    staff: StaffMember[];
    dates: string[];
    year: number;
    month: number;
    version?: {
        id: string;
        status: string;
        notes?: string;
    } | null;
    requests?: {
        leaves: Array<{
            id: string;
            userId: string;
            startDate: string;
            endDate: string;
            type: string;
            status: string;
            userName: string;
        }>;
        swaps: Array<{
            id: string;
            shiftId: string;
            date: string;
            requesterName: string;
            status: string;
        }>;
    };
}

interface MonthlyRosterViewProps {
    currentDate: Date;
    onDateChange: (date: Date) => void;
    versionId?: string;
    refreshKey?: number;
    fullscreenReadOnly?: boolean;
    onRosterStatusChange?: () => void | Promise<void>;
}

// DnD Components
const DraggableShift = ({ shift, children, isSelected, disabled }: { shift: Shift; children: React.ReactNode; isSelected?: boolean; disabled?: boolean }) => {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: shift.id,
        data: { shift },
        disabled,
    });

    const style = transform ? {
        transform: CSS.Translate.toString(transform),
        zIndex: 999,
        opacity: isDragging ? 0.5 : 1,
    } : undefined;

    return (
        <div ref={setNodeRef} style={style} {...(!disabled ? listeners : {})} {...(!disabled ? attributes : {})} className={`relative h-full w-full flex items-center justify-center ${isSelected ? 'ring-2 ring-blue-500 rounded-lg z-10' : ''}`}>
            {children}
        </div>
    );
};

const DroppableCell = ({ staffId, date, children, onCellClick, onDoubleClick, isWeekend, isSelectedCell }: { staffId: string; date: string; children: React.ReactNode, onCellClick: (e: React.MouseEvent) => void, onDoubleClick?: (e: React.MouseEvent) => void, isWeekend?: boolean, isSelectedCell?: boolean }) => {
    const { isOver, setNodeRef } = useDroppable({
        id: `${staffId}|${date}`,
        data: { staffId, date }
    });

    const style = {
        backgroundColor: isOver ? 'rgba(59, 130, 246, 0.1)' : undefined,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            onClick={onCellClick}
            onDoubleClick={onDoubleClick}
            className={`w-full h-full min-h-[50px] flex items-center justify-center transition-colors border-b border-r border-slate-100 ${isWeekend && !children ? 'bg-slate-50/50' : ''} ${isSelectedCell ? 'ring-2 ring-inset ring-blue-400 bg-blue-50/40' : ''}`}
        >
            {children}
        </div>
    );
};

const getAssignmentMarker = (shift: Shift) => {
    if (shift.needsCover) {
        return {
            label: 'Needs cover',
            className: 'bg-amber-100 text-amber-800 border-amber-300',
            ringClass: 'ring-2 ring-amber-400',
        };
    }

    const changeType = shift.assignmentChangeType?.toUpperCase();
    if (changeType === 'COVER') {
        return {
            label: 'Cover',
            className: 'bg-amber-100 text-amber-800 border-amber-300',
            ringClass: 'ring-2 ring-amber-400',
        };
    }

    if (changeType === 'SWAP') {
        return {
            label: 'Swap',
            className: 'bg-indigo-100 text-indigo-800 border-indigo-300',
            ringClass: 'ring-2 ring-indigo-400',
        };
    }

    if (shift.assignmentChanged) {
        return {
            label: 'Manual',
            className: 'bg-slate-100 text-slate-700 border-slate-300',
            ringClass: 'ring-2 ring-slate-400',
        };
    }

    return null;
};

const getShiftTitle = (shift: Shift) => {
    const parts = [`${shift.type} - ${shift.location || 'N/A'}`];

    if (shift.needsCover) {
        parts.push('Needs cover');
    }

    const changeType = shift.assignmentChangeType?.toUpperCase();
    if (changeType === 'COVER') {
        parts.push(`Cover${shift.originalUser?.name ? ` for ${shift.originalUser.name}` : ''}`);
    } else if (changeType === 'SWAP') {
        parts.push(`Swapped${shift.originalUser?.name ? ` from ${shift.originalUser.name}` : ''}`);
    } else if (shift.assignmentChanged) {
        parts.push(`Manual change${shift.originalUser?.name ? ` from ${shift.originalUser.name}` : ''}`);
    }

    return parts.join(' | ');
};

const MonthlyRosterView: React.FC<MonthlyRosterViewProps> = ({ currentDate, onDateChange, versionId, refreshKey, fullscreenReadOnly = false, onRosterStatusChange }) => {
    const [rosterData, setRosterData] = useState<MonthlyRosterData | null>(null);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedCell, setSelectedCell] = useState<{ staffId: string; date: string } | null>(null);
    const [selectedShiftIds, setSelectedShiftIds] = useState<string[]>([]);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingShift, setEditingShift] = useState<Shift | null>(null);
    const [dragError, setDragError] = useState<string | null>(null);
    const [clipboard, setClipboard] = useState<{ shifts: Shift[]; staffId: string } | null>(null);
    const [lastClickedCell, setLastClickedCell] = useState<{ staffId: string; date: string } | null>(null);
    const [bulkDeleting, setBulkDeleting] = useState(false);
    const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
    const [showUnpublishConfirm, setShowUnpublishConfirm] = useState(false);
    const [unpublishing, setUnpublishing] = useState(false);
    const [showNotes, setShowNotes] = useState(false);
    const [isMobilePortrait, setIsMobilePortrait] = useState(false);
    const [auditShiftId, setAuditShiftId] = useState<string | null>(null);
    const [auditData, setAuditData] = useState<ShiftAuditData | null>(null);
    const [auditLoading, setAuditLoading] = useState(false);
    const [auditError, setAuditError] = useState<string | null>(null);

    // Agency temp slots
    const [showAgencySlots, setShowAgencySlots] = useState(false);
    const [agencySlots, setAgencySlots] = useState<Array<{ label: string; assignedUser?: { id: string; name: string } }>>([
        { label: 'AgencyTemp1' },
        { label: 'AgencyTemp2' },
        { label: 'AgencyTemp3' },
    ]);
    const [activeSlotIndex, setActiveSlotIndex] = useState<number | null>(null);
    const [slotUsers, setSlotUsers] = useState<Array<{ id: string; name: string; email: string; isAgency?: boolean; agencyName?: string; role: { name: string } }>>([]);
    const [slotUsersLoading, setSlotUsersLoading] = useState(false);
    const [slotTempMode, setSlotTempMode] = useState(false);
    const [slotTempName, setSlotTempName] = useState('');
    const [slotTempUsername, setSlotTempUsername] = useState('');
    const [slotTempEmail, setSlotTempEmail] = useState('');
    const [slotTempAgency, setSlotTempAgency] = useState('');
    const [slotTempSkills, setSlotTempSkills] = useState<string[]>([]);
    const [slotAvailableSkills, setSlotAvailableSkills] = useState<Array<{ id: string; name: string }>>([]);
    const [slotRoles, setSlotRoles] = useState<Array<{ id: string; name: string }>>([]);
    const [slotSelectedRoleId, setSlotSelectedRoleId] = useState('');
    const [slotCreating, setSlotCreating] = useState(false);
    const [slotError, setSlotError] = useState('');

    const isPublished = rosterData?.version?.status === 'PUBLISHED';
    const isEditableDraft = rosterData?.version?.status === 'DRAFT';
    const isReadOnly = !isEditableDraft || isMobilePortrait || fullscreenReadOnly;
    const readOnlyMessage = fullscreenReadOnly
        ? 'Fullscreen roster view is read-only.'
        : isMobilePortrait
            ? 'Rotate your phone to landscape for the full roster grid. Portrait view is read-only.'
            : isPublished
                ? 'Roster is published and locked. Move it back to draft to make changes.'
                : 'Create or select a draft roster for this calendar month before adding shifts.';

    useEffect(() => {
        const updateReadOnly = () => setIsMobilePortrait(window.innerWidth < 768 && window.matchMedia('(orientation: portrait)').matches);
        updateReadOnly();
        window.addEventListener('resize', updateReadOnly);
        window.addEventListener('orientationchange', updateReadOnly);
        return () => {
            window.removeEventListener('resize', updateReadOnly);
            window.removeEventListener('orientationchange', updateReadOnly);
        };
    }, []);

    const handleShiftAction = (action: 'create' | 'update' | 'delete', data?: any) => {
        if (action === 'delete') {
            // Optimistic delete — instant feedback
            setRosterData(prev => {
                if (!prev) return null;
                const newData = { ...prev };
                const shift = data;
                const staff = newData.staff.find(s => s.id === shift.userId);
                if (staff && staff.shifts) {
                    const dateKey = shift.startTime.split('T')[0];
                    delete staff.shifts[dateKey];
                }
                return newData;
            });
        } else {
            // Create/Update: re-fetch from server for reliable data
            loadRoster();
        }
    };

    const sensors = useSensors(
        useSensor(MouseSensor, { activationConstraint: { distance: 10 } }),
        useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
    );

    const year = currentDate.getFullYear();
    const month = currentDate.getMonth() + 1;

    const loadRoster = useCallback(async () => {
        // Only show full loading spinner on initial load (no existing data)
        if (!rosterData) {
            setLoading(true);
        } else {
            setIsRefreshing(true);
        }
        setError(null);
        try {
            const data = await getMonthlyRoster(year, month, versionId);
            setRosterData(data);
        } catch (err: any) {
            const errorMessage = err.response?.data?.error?.message
                || err.response?.data?.message
                || err.message
                || 'Failed to load roster';
            setError(errorMessage);
            console.error('Error loading monthly roster:', err);
            // ... (rest of error logging)
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, [year, month, versionId, refreshKey]);

    useEffect(() => {
        loadRoster();
    }, [loadRoster]);

    const findShiftById = useCallback((shiftId: string): Shift | null => {
        if (!rosterData) return null;
        for (const staff of rosterData.staff) {
            for (const shift of Object.values(staff.shifts)) {
                if (shift.id === shiftId) return shift;
                const nested = shift._additionalShifts?.find(additional => additional.id === shiftId);
                if (nested) return nested;
            }
        }
        return null;
    }, [rosterData]);

    const openShiftAudit = async (shiftId: string) => {
        setAuditShiftId(shiftId);
        setAuditData(null);
        setAuditError(null);
        setAuditLoading(true);
        try {
            const response = await apiClient.get(`/shifts/${shiftId}/audit`);
            setAuditData(response.data?.data || response.data);
        } catch (err: any) {
            setAuditError(err.response?.data?.message || err.response?.data?.error?.message || 'Failed to load shift history');
        } finally {
            setAuditLoading(false);
        }
    };

    const closeShiftAudit = () => {
        setAuditShiftId(null);
        setAuditData(null);
        setAuditError(null);
    };

    const formatAuditDateTime = (value?: string | Date | null) => {
        if (!value) return 'Not recorded';
        return format(new Date(value), 'dd/MM/yyyy HH:mm');
    };

    const getAuditActionLabel = (action: string) => {
        const labels: Record<string, string> = {
            SHIFT_MARKED_NEEDS_COVER: 'Marked as needing cover',
            SHIFT_COVER_APPROVED: 'Cover approved',
            SHIFT_SWAP_APPROVED: 'Swap approved',
            SHIFT_MANUAL_ASSIGNMENT_CHANGED: 'Manual assignment changed',
        };
        return labels[action] || action.replace(/_/g, ' ').toLowerCase();
    };

    const scrollToShift = (shiftId: string) => {
        const element = document.querySelector(`[data-shift-id="${shiftId}"]`);
        element?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    };

    const handleCellClick = (staffId: string, date: string, e: React.MouseEvent) => {
        const staff = rosterData?.staff.find(s => s.id === staffId);
        const shift = staff?.shifts[date];

        // Guard: Do not allow clicks when roster is PUBLISHED
        if (isReadOnly) {
            if (shift && !fullscreenReadOnly) {
                void openShiftAudit(shift.id);
                return;
            }
            setDragError(readOnlyMessage);
            return;
        }

        // Shift+Click: Range selection (select all shifts between last click and current)
        if (e.shiftKey && lastClickedCell && lastClickedCell.staffId === staffId && staff) {
            const lastDate = parseISO(lastClickedCell.date);
            const currDate = parseISO(date);
            const startD = lastDate < currDate ? lastDate : currDate;
            const endD = lastDate < currDate ? currDate : lastDate;

            const rangeShiftIds: string[] = [];
            const d = new Date(startD);
            while (d <= endD) {
                const dKey = format(d, 'yyyy-MM-dd');
                if (staff.shifts[dKey]) {
                    rangeShiftIds.push(staff.shifts[dKey].id);
                }
                d.setDate(d.getDate() + 1);
            }
            setSelectedShiftIds(rangeShiftIds);
            return;
        }

        // Ctrl/Meta+Click: Toggle individual selection
        if ((e.ctrlKey || e.metaKey) && shift) {
            setSelectedShiftIds(prev => {
                const firstSelectedId = prev[0];
                if (firstSelectedId) {
                    const owningStaff = rosterData?.staff.find(s =>
                        Object.values(s.shifts).some(sh => sh.id === firstSelectedId)
                    );
                    if (owningStaff && owningStaff.id !== staffId) {
                        return [shift.id];
                    }
                }
                if (prev.includes(shift.id)) {
                    return prev.filter(id => id !== shift.id);
                }
                return [...prev, shift.id];
            });
            setLastClickedCell({ staffId, date });
            return;
        }

        // Standard Click — select/highlight only (double click opens modal)
        setLastClickedCell({ staffId, date });

        if (shift) {
            // Toggle selection of this shift
            setSelectedShiftIds(prev => {
                if (prev.includes(shift.id)) {
                    return prev.filter(id => id !== shift.id);
                }
                return [shift.id];
            });
            setSelectedCell({ staffId, date });
        } else {
            // Select empty cell
            setSelectedShiftIds([]);
            setSelectedCell({ staffId, date });
        }
    };

    const handleCellDoubleClick = (staffId: string, date: string, _e: React.MouseEvent) => {
        if (isReadOnly) {
            setDragError(readOnlyMessage);
            return;
        }

        if (!rosterData?.version?.id || rosterData.version.status !== 'DRAFT') {
            setDragError('Create or select a draft roster for this calendar month before adding shifts.');
            return;
        }

        const staff = rosterData?.staff.find(s => s.id === staffId);
        const shift = staff?.shifts[date];

        setLastClickedCell({ staffId, date });

        if (shift) {
            setEditingShift(shift);
            setShowEditModal(true);
        } else {
            setSelectedCell({ staffId, date });
            setEditingShift(null);
            setShowEditModal(true);
        }
    };

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (showEditModal) return; // Don't intercept while modal open

            // Escape: Clear selection
            if (e.key === 'Escape') {
                setSelectedShiftIds([]);
                setClipboard(null);
            }

            // Delete/Backspace: Delete selected shifts
            if ((e.key === 'Delete' || e.key === 'Backspace') && selectedShiftIds.length > 0) {
                if (isReadOnly) return;
                e.preventDefault();
                setShowBulkDeleteConfirm(true);
            }

            // Ctrl+C: Copy selected shifts
            if ((e.ctrlKey || e.metaKey) && e.key === 'c' && selectedShiftIds.length > 0) {
                if (isReadOnly) return;
                e.preventDefault();
                handleCopyBlock();
            }

            // Ctrl+A: Select all shifts for last-clicked user
            if ((e.ctrlKey || e.metaKey) && e.key === 'a' && lastClickedCell) {
                e.preventDefault();
                const staff = rosterData?.staff.find(s => s.id === lastClickedCell.staffId);
                if (staff) {
                    setSelectedShiftIds(Object.values(staff.shifts).map(s => s.id));
                }
            }

            // Ctrl+V: Paste copied shifts
            if ((e.ctrlKey || e.metaKey) && e.key === 'v' && clipboard && selectedCell) {
                if (isReadOnly) return;
                e.preventDefault();
                handlePasteBlock();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedShiftIds, lastClickedCell, showEditModal, rosterData, clipboard, selectedCell, isReadOnly]);

    // Copy selected shifts to clipboard
    const handleCopyBlock = () => {
        if (!rosterData || selectedShiftIds.length === 0) return;

        // Find the owner and shifts
        for (const staff of rosterData.staff) {
            const shifts = Object.values(staff.shifts).filter(s => selectedShiftIds.includes(s.id));
            if (shifts.length > 0) {
                setClipboard({ shifts, staffId: staff.id });
                break;
            }
        }
    };

    // Paste copied shifts onto the selected cell's user
    const handlePasteBlock = async () => {
        if (!clipboard || !selectedCell || !rosterData) return;

        const targetStaffId = selectedCell.staffId;
        const targetDate = new Date(selectedCell.date + 'T00:00:00');

        // Find the earliest date in the copied shifts to calculate offsets
        const copiedDates = clipboard.shifts.map(s => new Date(s.startTime));
        const earliestDate = new Date(Math.min(...copiedDates.map(d => d.getTime())));
        const earliestDateOnly = new Date(earliestDate.getFullYear(), earliestDate.getMonth(), earliestDate.getDate());

        // Calculate day offset from earliest copied shift to target date
        const dayOffset = Math.round((targetDate.getTime() - earliestDateOnly.getTime()) / (1000 * 60 * 60 * 24));

        let successCount = 0;
        let errorCount = 0;

        for (const shift of clipboard.shifts) {
            try {
                const originalStart = new Date(shift.startTime);
                const originalEnd = new Date(shift.endTime);

                // Offset the shift dates
                const newStart = new Date(originalStart);
                newStart.setDate(newStart.getDate() + dayOffset);
                const newEnd = new Date(originalEnd);
                newEnd.setDate(newEnd.getDate() + dayOffset);

                await createShift({
                    userId: targetStaffId,
                    startTime: newStart.toISOString(),
                    endTime: newEnd.toISOString(),
                    type: shift.type,
                    location: shift.location || undefined,
                    notes: (shift as any).notes || undefined,
                    versionId: rosterData.version?.id || undefined,
                });
                successCount++;
            } catch (err: any) {
                errorCount++;
                console.error('Failed to paste shift:', err);
            }
        }

        // Refresh the roster to show pasted shifts
        await loadRoster();

        if (errorCount > 0) {
            setDragError(`Pasted ${successCount} shift(s), ${errorCount} failed (likely overlap)`);
        }

        setSelectedShiftIds([]);
        setSelectedCell(null);
    };

    // Bulk delete selected shifts
    const handleBulkDelete = async () => {
        if (isReadOnly) return;
        if (!rosterData || selectedShiftIds.length === 0 || bulkDeleting) return;

        setShowBulkDeleteConfirm(false);
        setBulkDeleting(true);
        const previousData = JSON.parse(JSON.stringify(rosterData));

        // Optimistic removal
        setRosterData(prev => {
            if (!prev) return null;
            const newData = { ...prev };
            newData.staff.forEach(s => {
                Object.keys(s.shifts).forEach(d => {
                    if (selectedShiftIds.includes(s.shifts[d].id)) {
                        delete s.shifts[d];
                    }
                });
            });
            return newData;
        });

        try {
            await Promise.all(selectedShiftIds.map(id => deleteShift(id)));
            setSelectedShiftIds([]);
        } catch (err: any) {
            setDragError('Failed to delete: ' + (err.message || 'Unknown error'));
            setRosterData(previousData);
        } finally {
            setBulkDeleting(false);
        }
    };

    const handleDragEnd = async (event: DragEndEvent) => {
        // Guard: Block drag-drop when roster is published
        if (isReadOnly) {
            setDragError(readOnlyMessage);
            return;
        }

        const { active, over } = event;
        if (!over) return;

        const shiftId = active.id as string;
        const [targetStaffId, targetDateStr] = (over.id as string).split('|');

        if (!targetStaffId || !targetDateStr) return;

        // Find the shift and original data
        const originalShift = active.data.current?.shift as Shift;
        if (!originalShift) return;

        // Logic split: Multi-drag vs Single-drag
        const isMultiDrag = selectedShiftIds.includes(shiftId) && selectedShiftIds.length > 1;

        // Constraint: Shifts can only be moved within the same staff row (same user), not between rows
        if (targetStaffId !== originalShift.userId) {
            setDragError('Shifts can only be moved within the same staff row, not between different staff members.');
            return;
        }

        // Calculate delta
        const targetDate = parseISO(targetDateStr);
        const sourceDate = parseISO(originalShift.startTime);
        const dayDiff = differenceInCalendarDays(targetDate, sourceDate);

        // Shifts to move
        const shiftsToMoveIds = isMultiDrag ? selectedShiftIds : [shiftId];

        // Prepare updates
        const updates: { shiftId: string; updateData: any; oldShift: Shift }[] = [];
        const previousRosterData = JSON.parse(JSON.stringify(rosterData));

        // Optimistic Update Loop
        setRosterData(prev => {
            if (!prev) return null;
            const newData = { ...prev };

            shiftsToMoveIds.forEach(id => {
                let shift: Shift | undefined;
                let ownerStaff: StaffMember | undefined;

                for (const staff of newData.staff) {
                    const foundDate = Object.keys(staff.shifts).find(d => staff.shifts[d].id === id);
                    if (foundDate) {
                        shift = staff.shifts[foundDate];
                        ownerStaff = staff;
                        break;
                    }
                }

                if (shift && ownerStaff) {
                    const sStart = parseISO(shift.startTime);
                    const sEnd = parseISO(shift.endTime);
                    const newStart = addDays(sStart, dayDiff);
                    const durationMs = sEnd.getTime() - sStart.getTime();
                    const newEnd = new Date(newStart.getTime() + durationMs);
                    const newDateStr = format(newStart, 'yyyy-MM-dd');

                    const actualTargetStaffId = isMultiDrag ? ownerStaff.id : targetStaffId;

                    // Remove from old
                    const oldDateKey = shift.startTime.split('T')[0];
                    delete ownerStaff.shifts[oldDateKey];

                    // Add to new
                    const targetStaffObj = newData.staff.find(s => s.id === actualTargetStaffId);
                    if (targetStaffObj) {
                        if (!targetStaffObj.shifts) targetStaffObj.shifts = {};
                        const updatedShift = {
                            ...shift,
                            userId: actualTargetStaffId,
                            startTime: newStart.toISOString(),
                            endTime: newEnd.toISOString()
                        };
                        targetStaffObj.shifts[newDateStr] = updatedShift;

                        updates.push({
                            shiftId: id,
                            updateData: {
                                userId: actualTargetStaffId,
                                startTime: newStart.toISOString(),
                                endTime: newEnd.toISOString()
                            },
                            oldShift: shift
                        });
                    }
                }
            });
            return newData;
        });

        // Perform API calls
        try {
            await Promise.all(updates.map(u => updateShift(u.shiftId, u.updateData)));
        } catch (err: any) {
            setDragError('Failed to move shift(s): ' + (err.message || 'Unknown error'));
            setRosterData(previousRosterData); // Revert
        }
    };

    const handlePreviousMonth = () => {
        onDateChange(subMonths(currentDate, 1));
    };

    const handleNextMonth = () => {
        onDateChange(addMonths(currentDate, 1));
    };

    const handleToday = () => {
        onDateChange(new Date());
    };

    const hasLeaveOnDate = (staffId: string, date: string): { hasLeave: boolean; status?: string } => {
        if (!rosterData?.requests?.leaves) return { hasLeave: false };

        const dateObj = parseISO(date);
        const leave = rosterData.requests.leaves.find(l => {
            const leaveStart = parseISO(l.startDate);
            const leaveEnd = parseISO(l.endDate);
            return l.userId === staffId && dateObj >= leaveStart && dateObj <= leaveEnd;
        });

        return leave ? { hasLeave: true, status: leave.status } : { hasLeave: false };
    };

    const groupStaffByRole = (staff: StaffMember[]) => {
        const grouped: Record<string, StaffMember[]> = {
            'Controller': [],
            'Heathrow Clearer': [],
            'Gatwick Clearer': [],
            'Warehouse': [],
            'Office': [],
            'Other': [],
        };

        staff.forEach(member => {
            const role = member.role?.toUpperCase();

            if (role === 'CONTROLLER') {
                grouped['Controller'].push(member);
            } else if (role === 'WAREHOUSE') {
                grouped['Warehouse'].push(member);
            } else if (role === 'OFFICE' || role === 'ADMIN' || role === 'MANAGER') {
                grouped['Office'].push(member);
            } else if (role === 'CLEARER') {
                const skills = (member as any).userSkills?.map((us: any) => us.skill?.name) || [];
                const isLHR = skills.some((s: string) => s?.includes('LHR') || s?.includes('Heathrow'));
                const isLGW = skills.some((s: string) => s?.includes('LGW') || s?.includes('Gatwick'));

                if (isLHR) {
                    grouped['Heathrow Clearer'].push(member);
                } else if (isLGW) {
                    grouped['Gatwick Clearer'].push(member);
                } else {
                    grouped['Heathrow Clearer'].push(member);
                }
            } else {
                grouped['Other'].push(member);
            }
        });

        Object.keys(grouped).forEach(key => {
            grouped[key].sort((a, b) => a.name.localeCompare(b.name));
        });

        return grouped;
    };

    if (loading && !rosterData) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-4 text-slate-600">Loading roster...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <div className="flex items-start gap-3">
                    <svg className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="flex-1">
                        <h3 className="text-red-800 font-semibold mb-1">Failed to Load Roster</h3>
                        <p className="text-red-600 text-sm mb-3">{error}</p>
                        <button
                            onClick={loadRoster}
                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (!rosterData) {
        return (
            <div className="text-center p-12 bg-white rounded-lg shadow-sm border border-slate-200">
                <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Roster Data Available</h3>
                <p className="text-slate-500 mb-4">No shifts have been created for {format(currentDate, 'MMMM yyyy')} yet.</p>
                <button
                    onClick={loadRoster}
                    className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                    Refresh
                </button>
            </div>
        );
    }

    // Check if roster is empty (no staff or no shifts)
    const hasShifts = rosterData.staff.some(staff =>
        Object.keys(staff.shifts || {}).length > 0
    );

    if (!hasShifts && rosterData.staff.length === 0) {
        return (
            <div className="text-center p-12 bg-white rounded-lg shadow-sm border border-slate-200">
                <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No Staff Members</h3>
                <p className="text-slate-500 mb-4">There are no active staff members in the system.</p>
            </div>
        );
    }

    const groupedStaff = groupStaffByRole(rosterData.staff);
    const monthName = format(currentDate, 'MMMM yyyy');
    const rosterNotes = rosterData.version?.notes?.trim() || '';
    const showRosterNotesControl = rosterNotes.length > 0 && !/^auto-generated via intelligent roster wizard$/i.test(rosterNotes);
    const auditShift = auditData?.shift || (auditShiftId ? findShiftById(auditShiftId) : null);
    const auditMarker = auditShift ? getAssignmentMarker(auditShift) : null;
    const allRosterShifts = rosterData.staff.flatMap(staff =>
        Object.values(staff.shifts).flatMap(shift => [shift, ...(shift._additionalShifts || [])])
    );
    const getSwapPair = (shift: Shift | null | undefined) => {
        if (!shift || shift.assignmentChangeType?.toUpperCase() !== 'SWAP' || !shift.assignmentSourceSwapId) return null;
        return allRosterShifts.find(candidate =>
            candidate.id !== shift.id &&
            candidate.assignmentChangeType?.toUpperCase() === 'SWAP' &&
            candidate.assignmentSourceSwapId === shift.assignmentSourceSwapId
        ) || auditData?.relatedShifts?.find(candidate =>
            candidate.id !== shift.id &&
            candidate.assignmentChangeType?.toUpperCase() === 'SWAP' &&
            candidate.assignmentSourceSwapId === shift.assignmentSourceSwapId
        ) || null;
    };
    const getSwapPairLabel = (shift: Shift) => {
        const pair = getSwapPair(shift);
        if (!pair) return 'Swap';
        return `Swap with ${pair.user?.name || 'staff'}`;
    };
    const auditSwapPair = getSwapPair(auditShift);
    const activeSwapSourceId = auditShift?.assignmentChangeType?.toUpperCase() === 'SWAP'
        ? auditShift.assignmentSourceSwapId
        : null;
    const auditSwapPairInView = Boolean(auditSwapPair && allRosterShifts.some(shift => shift.id === auditSwapPair.id));

    return (
        <>
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
            <div className={`${fullscreenReadOnly ? 'space-y-2' : 'space-y-4'} relative`} style={{ opacity: isRefreshing ? 0.6 : 1, transition: 'opacity 0.15s ease' }}>
                {isRefreshing && (
                    <div className="absolute top-2 right-2 z-40 flex items-center gap-2 bg-white/80 backdrop-blur rounded-lg px-3 py-1.5 shadow-sm">
                        <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        <span className="text-xs text-slate-500">Updating...</span>
                    </div>
                )}
                {dragError && (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                        <span className="block sm:inline">{dragError}</span>
                        <span className="absolute top-0 bottom-0 right-0 px-4 py-3" onClick={() => setDragError(null)}>
                            <svg className="fill-current h-6 w-6 text-red-500" role="button" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z" /></svg>
                        </span>
                    </div>
                )}



                {/* Navigation — clean 3-section layout */}
                <div className={`sticky ${fullscreenReadOnly ? 'top-0' : 'top-[8.5rem]'} z-30 flex items-center justify-between bg-white px-4 py-3 rounded-xl shadow-sm border border-slate-200`}>
                    {/* Left: Month navigation */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handlePreviousMonth}
                            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-800"
                            title="Previous month"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                        </button>
                        <h2 className="text-lg font-bold text-slate-800 select-none">
                            {monthName}
                        </h2>
                        <button
                            onClick={handleNextMonth}
                            className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors text-slate-500 hover:text-slate-800"
                            title="Next month"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                        </button>
                        {rosterData?.version?.status === 'DRAFT' && (
                            <span className="ml-1 px-2 py-0.5 text-[11px] font-bold bg-amber-100 text-amber-800 rounded-full border border-amber-200">
                                DRAFT
                            </span>
                        )}
                        {rosterData?.version?.status === 'PUBLISHED' && (
                            <span className="ml-1 px-2 py-0.5 text-[11px] font-bold bg-red-100 text-red-700 rounded-full border border-red-200 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                                LOCKED
                            </span>
                        )}
                    </div>

                    {/* Center: Inline legend */}
                    <div className="hidden md:flex items-center gap-4 text-xs">
                        {Object.values(ShiftTheme).filter((t: any) => t.icon).map((theme: any, idx) => (
                            <div key={idx} className="flex items-center gap-1 text-slate-500">
                                <span className="text-sm">{theme.icon}</span>
                                <span className="font-medium">{theme.label}</span>
                            </div>
                        ))}
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleToday}
                            className="px-3 py-1.5 text-xs font-medium bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors"
                        >
                            Today
                        </button>
                        {!isReadOnly && (
                            <button
                                onClick={() => setShowAgencySlots(prev => !prev)}
                                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                                    showAgencySlots
                                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                                        : 'bg-orange-50 text-orange-600 border border-orange-200 hover:bg-orange-100'
                                }`}
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                {showAgencySlots ? 'Hide Temps' : 'Agency Temp'}
                            </button>
                        )}
                    </div>
                </div>

                {/* PUBLISHED LOCK BANNER */}
                {!fullscreenReadOnly && isPublished && (
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 p-5 rounded-xl border-2 border-red-200 shadow-sm">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center border border-red-200">
                                    <svg className="w-6 h-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="text-lg font-bold text-red-800">Roster is Published & Locked</h3>
                                    <p className="text-sm text-red-600 mt-0.5">This roster is visible to all staff. Editing is disabled. Move it back to draft before making changes.</p>
                                </div>
                            </div>
                            <button
                                onClick={() => setShowUnpublishConfirm(true)}
                                className="px-6 py-3 text-sm font-bold bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all shadow-lg shadow-amber-500/20 flex items-center gap-2 whitespace-nowrap"
                            >
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
                                </svg>
                                Move to Draft
                            </button>
                        </div>
                    </div>
                )}

                {!fullscreenReadOnly && isMobilePortrait && (
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200 text-sm text-blue-800">
                        Portrait phone view is read-only. Rotate to landscape for the full roster grid.
                    </div>
                )}

                {/* UNPUBLISH CONFIRMATION MODAL */}
                {showUnpublishConfirm && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUnpublishConfirm(false)}>
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden" onClick={e => e.stopPropagation()}>
                            <div className="bg-amber-50 border-b border-amber-200 p-6">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center border-2 border-amber-300">
                                        <svg className="w-6 h-6 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-bold text-amber-900">Move Roster to Draft?</h3>
                                        <p className="text-sm text-amber-700">This will unpublish the roster</p>
                                    </div>
                                </div>
                            </div>
                            <div className="p-6 space-y-4">
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                    <p className="text-sm text-red-800 font-semibold mb-2">Warning — this action will:</p>
                                    <ul className="text-sm text-red-700 space-y-1 list-disc list-inside">
                                        <li>Remove the roster from staff view immediately</li>
                                        <li>Allow editing of all shifts</li>
                                        <li>Require re-publishing to make it visible again</li>
                                    </ul>
                                </div>
                                <div className="flex gap-3 pt-2">
                                    <button
                                        onClick={() => setShowUnpublishConfirm(false)}
                                        className="flex-1 px-4 py-3 text-sm font-semibold bg-slate-100 text-slate-700 rounded-xl hover:bg-slate-200 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={async () => {
                                            if (!rosterData?.version?.id) return;
                                            setUnpublishing(true);
                                            try {
                                                await unpublishRosterVersion(rosterData.version.id);
                                                setShowUnpublishConfirm(false);
                                                await loadRoster();
                                                await onRosterStatusChange?.();
                                            } catch (err: any) {
                                                setDragError('Failed to unpublish: ' + (err.message || 'Unknown error'));
                                            } finally {
                                                setUnpublishing(false);
                                            }
                                        }}
                                        disabled={unpublishing}
                                        className="flex-1 px-4 py-3 text-sm font-bold bg-amber-500 text-white rounded-xl hover:bg-amber-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                    >
                                        {unpublishing ? (
                                            <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> Moving...</>
                                        ) : (
                                            <>Yes, Move to Draft</>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Roster Notes — collapsible */}
                {!fullscreenReadOnly && showRosterNotesControl && (
                    <button
                        onClick={() => setShowNotes(prev => !prev)}
                        className="w-full flex items-center gap-2 px-4 py-2 bg-blue-50 hover:bg-blue-100 rounded-lg border border-blue-200 transition-colors text-left"
                    >
                        <svg className="w-4 h-4 text-blue-600 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <span className="text-xs font-semibold text-blue-800 flex-1">Roster Notes</span>
                        <svg className={`w-3.5 h-3.5 text-blue-500 transition-transform ${showNotes ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>
                )}
                {!fullscreenReadOnly && showNotes && showRosterNotesControl && (
                    <div className="px-4 py-3 bg-blue-50/50 rounded-lg border border-blue-100 -mt-2">
                        <p className="text-xs text-blue-800 whitespace-pre-wrap">{rosterNotes}</p>
                    </div>
                )}

                {/* Roster Grid */}
                <div className={`bg-white rounded-lg shadow-sm border border-slate-200 overflow-auto overscroll-contain relative isolate ${fullscreenReadOnly ? 'max-h-[calc(100dvh-8.5rem)]' : 'max-h-[calc(100vh-14rem)]'} ${isReadOnly ? 'opacity-90' : ''}`}>
                    {!fullscreenReadOnly && isPublished && (
                        <div className="pointer-events-none absolute inset-0 z-20" title="Roster is published and locked. Click a shift to view details." />
                    )}
                    <table className="w-full border-separate border-spacing-0">
                        <thead>
                            <tr>
                                <th className="sticky top-0 left-0 z-50 bg-slate-50 px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-r border-slate-200 min-w-[200px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1),0_8px_14px_-14px_rgba(15,23,42,0.45)]">
                                    Staff Member
                                </th>
                                <th className="sticky top-0 left-[200px] z-50 bg-slate-50 px-2 py-3 text-center text-xs font-bold text-slate-500 uppercase tracking-wider border-b border-r border-slate-200 min-w-[60px] shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1),0_8px_14px_-14px_rgba(15,23,42,0.45)]">
                                    Shifts
                                </th>
                                {rosterData.dates.map((date) => {
                                    const dateObj = parseISO(date);
                                    const dayOfWeek = format(dateObj, 'EEE');
                                    const dayNum = format(dateObj, 'd');
                                    const isWeekend = getDay(dateObj) === 0 || getDay(dateObj) === 6;
                                    const isToday = format(new Date(), 'yyyy-MM-dd') === date;

                                    return (
                                        <th
                                            key={date}
                                            className={`sticky top-0 z-40 px-1 py-3 text-center border-b border-r border-slate-200 min-w-[65px] transition-colors shadow-[0_8px_14px_-14px_rgba(15,23,42,0.45)] ${isToday ? 'bg-blue-50 ring-inset ring-2 ring-blue-500' : isWeekend ? 'bg-slate-50' : 'bg-white'
                                                }`}
                                        >
                                            <div className="text-[10px] text-slate-400 font-semibold uppercase">{dayOfWeek}</div>
                                            <div className={`text-sm font-bold ${isWeekend ? 'text-slate-500' : 'text-slate-700'}`}>{dayNum}</div>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(groupedStaff).filter(([_, staff]) => staff.length > 0).map(([role, staffList], groupIndex) => (
                                <React.Fragment key={role}>
                                    {groupIndex > 0 && (
                                        <tr className="h-6 bg-slate-50"><td colSpan={rosterData.dates.length + 2}></td></tr>
                                    )}
                                    <tr className="bg-slate-200 border-y-2 border-slate-300 sticky left-0 z-10 shadow-sm">
                                        <td colSpan={rosterData.dates.length + 2} className="px-4 py-2 text-sm font-bold text-slate-700 uppercase tracking-wider">
                                            {role}s
                                        </td>
                                    </tr>
                                    {staffList.map((staff) => {
                                        const shiftCount = Object.keys(staff.shifts).length;
                                        const isAgency = (staff as any).isAgency;
                                        const agencyName = (staff as any).agencyName;

                                        return (
                                            <tr key={staff.id} className="border-b border-slate-100 hover:bg-slate-50/80 transition-colors group">
                                                <td className="sticky left-0 z-10 bg-white group-hover:bg-slate-50 px-4 py-3 font-medium text-slate-700 text-sm border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                                                    <div className="flex items-center gap-2">
                                                        <span>{staff.name}</span>
                                                        {isAgency && (
                                                            <span title={`Temp Staff${agencyName ? ` — ${agencyName}` : ''}`} className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-100 text-orange-600 border border-orange-200 text-[10px] font-bold cursor-help">
                                                                T
                                                            </span>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="sticky left-[200px] z-10 bg-white group-hover:bg-slate-50 px-2 py-3 text-center text-xs font-bold text-slate-500 border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                                                    <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${shiftCount < 10 ? 'bg-red-100 text-red-600' : 'bg-slate-100 text-slate-600'}`}>
                                                        {shiftCount}
                                                    </span>
                                                </td>
                                                {rosterData.dates.map((date) => {
                                                    const shift = staff.shifts[date];
                                                    const dateObj = parseISO(date);
                                                    const isWeekend = getDay(dateObj) === 0 || getDay(dateObj) === 6;
                                                    const leaveInfo = hasLeaveOnDate(staff.id, date);
                                                    const style = leaveInfo.hasLeave
                                                        ? getLeaveStyle(leaveInfo.status!)
                                                        : getShiftStyle(shift, date);

                                                    const isSelected = shift && selectedShiftIds.includes(shift.id);

                                                    return (
                                                        <td
                                                            key={date}
                                                            className="px-0 py-0 border-b border-r border-slate-100 relative h-[50px]"
                                                        >
                                                            <DroppableCell staffId={staff.id} date={date} onCellClick={(e) => handleCellClick(staff.id, date, e)} onDoubleClick={(e) => handleCellDoubleClick(staff.id, date, e)} isWeekend={isWeekend} isSelectedCell={selectedCell?.staffId === staff.id && selectedCell?.date === date}>
                                                                {leaveInfo.hasLeave && (
                                                                    <div className={`w-full h-full flex items-center justify-center ${style.base} ${style.hover}`} title={`${leaveInfo.status} Leave`}>
                                                                        <span className="text-lg">{style.icon}</span>
                                                                    </div>
                                                                )}
                                                                {shift && !leaveInfo.hasLeave && (
                                                                    <DraggableShift shift={shift} isSelected={isSelected} disabled={isReadOnly}>
                                                                        <div className={`grid w-full h-full ${(shift._additionalShifts?.length || 0) > 0 ? 'grid-cols-2' : 'grid-cols-1'} items-center justify-center gap-1 px-1`}>
                                                                            {[shift, ...(shift._additionalShifts || [])].slice(0, 2).map((cellShift, idx) => {
                                                                                const cellStyle = idx === 0 ? style : getShiftStyle(cellShift, date);
                                                                                const assignmentMarker = getAssignmentMarker(cellShift);
                                                                                const swapPairLabel = cellShift.assignmentChangeType?.toUpperCase() === 'SWAP' ? getSwapPairLabel(cellShift) : null;
                                                                                const isLinkedSwap = Boolean(activeSwapSourceId && cellShift.assignmentSourceSwapId === activeSwapSourceId);
                                                                                return (
                                                                                    <div
                                                                                        key={cellShift.id}
                                                                                        data-shift-id={cellShift.id}
                                                                                        className={`relative h-8 min-w-0 rounded-md flex items-center justify-center ${isReadOnly ? 'cursor-pointer' : 'cursor-move'} text-[10px] font-semibold shadow-sm transition-all border ${cellStyle.base} ${cellStyle.hover} ${assignmentMarker?.ringClass || ''} ${isLinkedSwap ? 'outline outline-4 outline-indigo-200 z-20' : ''}`}
                                                                                        title={swapPairLabel ? `${getShiftTitle(cellShift)} | ${swapPairLabel}` : getShiftTitle(cellShift)}
                                                                                    >
                                                                                        <span className={`truncate px-1 ${assignmentMarker ? 'pt-2' : ''}`}>{getShiftAbbreviation(cellShift)}</span>
                                                                                        {assignmentMarker && (
                                                                                            <span className={`absolute left-1 top-0.5 max-w-[calc(100%-0.5rem)] truncate rounded-full border px-1 text-[7px] font-black uppercase leading-3 tracking-wide ${assignmentMarker.className}`}>
                                                                                                {swapPairLabel || assignmentMarker.label}
                                                                                            </span>
                                                                                        )}
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                            {(shift._additionalShifts?.length || 0) > 1 && (
                                                                                <span className="absolute bottom-0.5 right-1 text-[9px] font-bold text-slate-500">
                                                                                    +{(shift._additionalShifts?.length || 0) - 1}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    </DraggableShift>
                                                                )}
                                                            </DroppableCell>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </React.Fragment>
                            ))}

                            {/* AGENCY TEMP ROWS */}
                            {showAgencySlots && rosterData && (
                                <>
                                    <tr className="h-4 bg-slate-50"><td colSpan={rosterData.dates.length + 2}></td></tr>
                                    <tr className="bg-orange-100 border-y-2 border-orange-300">
                                        <td colSpan={rosterData.dates.length + 2} className="px-4 py-2 text-sm font-bold text-orange-800 uppercase tracking-wider flex items-center gap-2">
                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                            Agency Temp Staff
                                        </td>
                                    </tr>
                                    {agencySlots.map((slot, slotIdx) => (
                                        <tr key={`agency-${slotIdx}`} className="border-b border-orange-100 hover:bg-orange-50/30 transition-colors group">
                                            <td
                                                className="sticky left-0 z-10 bg-white group-hover:bg-orange-50/50 px-4 py-3 font-medium text-sm border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)] cursor-pointer"
                                                onClick={() => {
                                                    if (isReadOnly) {
                                                        setDragError(readOnlyMessage);
                                                        return;
                                                    }
                                                    setActiveSlotIndex(slotIdx);
                                                    setSlotTempMode(false);
                                                    setSlotError('');
                                                    setSlotTempName('');
                                                    setSlotTempUsername('');
                                                    setSlotTempEmail('');
                                                    setSlotTempAgency('');
                                                    setSlotTempSkills([]);
                                                    // Load users for selection
                                                    setSlotUsersLoading(true);
                                                    Promise.all([
                                                        apiClient.get('/users', { params: { limit: 100 } }),
                                                        apiClient.get('/users/skills/list'),
                                                        apiClient.get('/roles'),
                                                    ]).then(([usersRes, skillsRes, rolesRes]) => {
                                                        setSlotUsers(usersRes.data.data || usersRes.data.users || []);
                                                        setSlotAvailableSkills(skillsRes.data.data || []);
                                                        const allRoles = rolesRes.data.data || [];
                                                        setSlotRoles(allRoles);
                                                        const clearer = allRoles.find((r: any) => r.name.toUpperCase().includes('CLEARER'));
                                                        setSlotSelectedRoleId(clearer?.id || allRoles[0]?.id || '');
                                                    }).finally(() => setSlotUsersLoading(false));
                                                }}
                                            >
                                                <div className="flex items-center gap-2">
                                                    {slot.assignedUser ? (
                                                        <>
                                                            <span>{slot.assignedUser.name}</span>
                                                            <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-orange-100 text-orange-600 border border-orange-200 text-[10px] font-bold">T</span>
                                                        </>
                                                    ) : (
                                                        <span className="text-orange-400 italic flex items-center gap-1.5">
                                                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                                            {slot.label} — click to assign
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="sticky left-[200px] z-10 bg-white group-hover:bg-orange-50/50 px-2 py-3 text-center text-xs font-bold text-slate-400 border-b border-r border-slate-200 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.05)]">
                                                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-slate-100 text-slate-400">0</span>
                                            </td>
                                            {rosterData.dates.map((date) => {
                                                const dateObj = parseISO(date);
                                                const isWeekend = getDay(dateObj) === 0 || getDay(dateObj) === 6;
                                                return (
                                                    <td key={date} className={`px-1 py-2 text-center border-b border-r border-slate-100 min-w-[65px] ${isWeekend ? 'bg-slate-50/30' : ''}`}>
                                                        {slot.assignedUser ? (
                                                            <DroppableCell
                                                                staffId={slot.assignedUser.id}
                                                                date={date}
                                                                isWeekend={isWeekend}
                                                                onCellClick={() => {
                                                    if (isReadOnly) return;
                                                                    setSelectedCell({ staffId: slot.assignedUser!.id, date });
                                                                    setEditingShift(null);
                                                                    setShowEditModal(true);
                                                                }}
                                                            >
                                                                {null}
                                                            </DroppableCell>
                                                        ) : (
                                                            <div className="min-h-[50px] flex items-center justify-center">
                                                                <span className="text-slate-200">—</span>
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>

                <DragOverlay>
                    {/* Optional: Add custom drag preview if needed */}
                </DragOverlay>

                {/* Edit Shift Modal */}
                {showEditModal && (
                    <EditShiftModal
                        shift={editingShift ? { ...editingShift, status: 'scheduled', isPublished: false } as any : null}
                        staffId={selectedCell?.staffId}
                        date={selectedCell?.date || editingShift?.startTime}
                        versionId={rosterData.version?.id || undefined}
                        onClose={() => {
                            setShowEditModal(false);
                            setEditingShift(null);
                            setSelectedCell(null);
                        }}
                        onSuccess={handleShiftAction}
                    />
                )}

                {/* Agency Slot Assignment Modal */}
                {activeSlotIndex !== null && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setActiveSlotIndex(null)}>
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                            {/* Header */}
                            <div className="bg-orange-50 border-b border-orange-200 p-5 flex-shrink-0">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center border border-orange-200">
                                            <svg className="w-5 h-5 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" /></svg>
                                        </div>
                                        <div>
                                            <h3 className="text-base font-bold text-orange-900">Assign {agencySlots[activeSlotIndex]?.label}</h3>
                                            <p className="text-xs text-orange-600">Select existing staff or create new</p>
                                        </div>
                                    </div>
                                    <button onClick={() => setActiveSlotIndex(null)} className="w-8 h-8 rounded-lg hover:bg-orange-100 flex items-center justify-center">
                                        <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                            </div>

                            {/* Body */}
                            <div className="p-5 space-y-4 overflow-y-auto flex-1">
                                {slotError && <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-lg text-sm">{slotError}</div>}

                                {slotUsersLoading ? (
                                    <div className="text-center py-6 text-slate-400 text-sm">Loading staff...</div>
                                ) : !slotTempMode ? (
                                    <>
                                        {/* Select Existing User */}
                                        <div>
                                            <label className="block text-sm font-semibold text-slate-700 mb-2">Select Existing Staff</label>
                                            <select
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                                                defaultValue=""
                                                onChange={(e) => {
                                                    const user = slotUsers.find(u => u.id === e.target.value);
                                                    if (user) {
                                                        setAgencySlots(prev => prev.map((s, i) => i === activeSlotIndex ? { ...s, assignedUser: { id: user.id, name: user.name } } : s));
                                                        setActiveSlotIndex(null);
                                                    }
                                                }}
                                            >
                                                <option value="" disabled>Choose a staff member...</option>
                                                {slotUsers.filter(u => !u.isAgency).length > 0 && (
                                                    <optgroup label="Staff">
                                                        {slotUsers.filter(u => !u.isAgency).map(u => (
                                                            <option key={u.id} value={u.id}>{u.name} ({u.role.name})</option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                                {slotUsers.filter(u => u.isAgency).length > 0 && (
                                                    <optgroup label="⏱ Temp Agency Staff">
                                                        {slotUsers.filter(u => u.isAgency).map(u => (
                                                            <option key={u.id} value={u.id}>{u.name} — {u.agencyName || 'Temp'}</option>
                                                        ))}
                                                    </optgroup>
                                                )}
                                            </select>
                                        </div>

                                        <div className="flex items-center gap-3 text-slate-300">
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                            <span className="text-xs font-medium">OR</span>
                                            <div className="flex-1 h-px bg-slate-200"></div>
                                        </div>

                                        {/* Create New Button */}
                                        <button
                                            type="button"
                                            onClick={() => setSlotTempMode(true)}
                                            className="w-full px-4 py-3 text-sm font-semibold text-orange-600 bg-orange-50 border-2 border-dashed border-orange-200 rounded-xl hover:bg-orange-100 transition-colors flex items-center justify-center gap-2"
                                        >
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" /></svg>
                                            Create New Temp Staff
                                        </button>
                                    </>
                                ) : (
                                    /* Create Temp Form */
                                    <div className="space-y-3">
                                        <button type="button" onClick={() => setSlotTempMode(false)} className="text-xs text-orange-500 hover:text-orange-700 flex items-center gap-1 font-semibold">
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                                            Back to selection
                                        </button>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">Name *</label>
                                            <input
                                                type="text"
                                                value={slotTempName}
                                                onChange={e => setSlotTempName(e.target.value)}
                                                placeholder="e.g., John Smith"
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                                                autoFocus
                                            />
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-600 mb-1">Username</label>
                                                <input
                                                    type="text"
                                                    value={slotTempUsername}
                                                    onChange={e => setSlotTempUsername(e.target.value)}
                                                    placeholder="Auto if blank"
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                                                />
                                            </div>
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-600 mb-1">Email</label>
                                                <input
                                                    type="email"
                                                    value={slotTempEmail}
                                                    onChange={e => setSlotTempEmail(e.target.value)}
                                                    placeholder="Auto if blank"
                                                    className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">Agency Name</label>
                                            <input
                                                type="text"
                                                value={slotTempAgency}
                                                onChange={e => setSlotTempAgency(e.target.value)}
                                                placeholder="Optional"
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none text-sm"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs font-semibold text-slate-600 mb-1">Role</label>
                                            <select
                                                value={slotSelectedRoleId}
                                                onChange={e => setSlotSelectedRoleId(e.target.value)}
                                                className="w-full px-3 py-2.5 rounded-xl border border-slate-200 focus:border-orange-400 outline-none text-sm"
                                            >
                                                {slotRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                                            </select>
                                        </div>

                                        {slotAvailableSkills.length > 0 && (
                                            <div>
                                                <label className="block text-xs font-semibold text-slate-600 mb-1">Skills</label>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {slotAvailableSkills.map(skill => (
                                                        <button
                                                            key={skill.id}
                                                            type="button"
                                                            onClick={() => setSlotTempSkills(prev => prev.includes(skill.id) ? prev.filter(id => id !== skill.id) : [...prev, skill.id])}
                                                            className={`px-2.5 py-1 rounded-lg text-xs font-semibold transition-all border ${
                                                                slotTempSkills.includes(skill.id)
                                                                    ? 'bg-orange-200 border-orange-400 text-orange-900'
                                                                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                                                            }`}
                                                        >
                                                            {slotTempSkills.includes(skill.id) && '✓ '}{skill.name}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <button
                                            type="button"
                                            disabled={slotCreating || !slotTempName.trim()}
                                            onClick={async () => {
                                                const roleId = slotSelectedRoleId || slotRoles[0]?.id;
                                                if (!slotTempName.trim() || !roleId) {
                                                    setSlotError(!slotTempName.trim() ? 'Name is required' : 'No role available — please close and reopen');
                                                    return;
                                                }
                                                setSlotCreating(true);
                                                setSlotError('');
                                                const nameParts = slotTempName.trim().split(/\s+/);
                                                const firstName = nameParts[0] || '';
                                                const lastName = nameParts.slice(1).join('') || '';
                                                const loginName = `${firstName}${lastName}T1`.replace(/[^a-zA-Z0-9._-]/g, '');
                                                const username = slotTempUsername.trim() || loginName;
                                                const email = slotTempEmail.trim() || `${loginName}@eagle.local`;
                                                try {
                                                    const createRes = await apiClient.post('/users', {
                                                        name: slotTempName.trim(),
                                                        username,
                                                        email,
                                                        password: 'apple100',
                                                        roleId,
                                                        skillIds: slotTempSkills,
                                                        isAgency: true,
                                                        agencyName: slotTempAgency.trim() || 'Temp Agency',
                                                    });
                                                    const newUser = createRes.data.data;
                                                    if (newUser) {
                                                        setAgencySlots(prev => prev.map((s, i) => i === activeSlotIndex
                                                            ? { ...s, assignedUser: { id: newUser.id, name: slotTempName.trim() } }
                                                            : s
                                                        ));
                                                        setActiveSlotIndex(null);
                                                    }
                                                } catch (err: any) {
                                                    const errData = err.response?.data?.error;
                                                    setSlotError(errData?.details || errData?.message || err.message || 'Failed to create user');
                                                } finally {
                                                    setSlotCreating(false);
                                                }
                                            }}
                                            className="w-full px-4 py-3 text-sm font-bold bg-orange-500 text-white rounded-xl hover:bg-orange-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                                        >
                                            {slotCreating ? (
                                                <><div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div> Creating...</>
                                            ) : (
                                                'Create & Assign'
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Fixed Bottom Selection Toolbar */}
                {selectedShiftIds.length > 0 && (
                    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-slate-900/95 backdrop-blur-md text-white rounded-2xl shadow-2xl px-5 py-3 flex items-center gap-4 transition-all animate-in slide-in-from-bottom-4" style={{ animation: 'slideUp 0.2s ease-out' }}>
                        {/* Selection count */}
                        <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-lg bg-blue-500/20 flex items-center justify-center">
                                <span className="text-blue-400 font-bold text-sm">{selectedShiftIds.length}</span>
                            </div>
                            <span className="text-sm font-medium text-slate-300">
                                shift{selectedShiftIds.length > 1 ? 's' : ''}
                            </span>
                        </div>

                        <div className="w-px h-6 bg-slate-700"></div>

                        {selectedShiftIds.length === 1 && (
                            <button
                                onClick={() => openShiftAudit(selectedShiftIds[0])}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-500/20 hover:bg-blue-500/30 text-blue-200 rounded-lg transition-colors"
                                title="Open shift details and audit history"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                                Details
                            </button>
                        )}

                        {/* Copy */}
                        <button
                            onClick={handleCopyBlock}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-slate-700/50 hover:bg-slate-600 text-slate-200 rounded-lg transition-colors"
                            title="Copy selected shifts"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                            Copy
                            <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-slate-600 rounded text-slate-400">⌘C</kbd>
                        </button>

                        {/* Delete */}
                        <button
                            onClick={() => setShowBulkDeleteConfirm(true)}
                            disabled={bulkDeleting}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg transition-colors disabled:opacity-50"
                            title="Delete selected shifts"
                        >
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                            {bulkDeleting ? 'Deleting...' : 'Delete'}
                            <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-slate-600 rounded text-slate-400">⌫</kbd>
                        </button>

                        {/* Clipboard indicator */}
                        {clipboard && (
                            <span className="text-xs text-emerald-400 bg-emerald-500/15 px-2 py-1 rounded-lg">
                                📋 {clipboard.shifts.length} copied
                            </span>
                        )}

                        {/* Paste */}
                        {clipboard && selectedCell && (
                            <button
                                onClick={handlePasteBlock}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded-lg transition-colors"
                                title="Paste shifts here"
                            >
                                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                                Paste
                                <kbd className="ml-1 px-1 py-0.5 text-[10px] bg-slate-600 rounded text-slate-400">⌘V</kbd>
                            </button>
                        )}

                        <div className="w-px h-6 bg-slate-700"></div>

                        {/* Hints */}
                        <div className="hidden lg:flex items-center gap-3 text-[10px] text-slate-500">
                            <span>⇧ Range</span>
                            <span>⌘ Multi</span>
                        </div>

                        {/* Clear */}
                        <button
                            onClick={() => { setSelectedShiftIds([]); setSelectedCell(null); }}
                            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors"
                            title="Clear selection"
                        >
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                )}
            </div>
        </DndContext >

            {auditShiftId && (
                <div className="fixed inset-0 z-[70] flex justify-end bg-slate-900/30" onClick={closeShiftAudit}>
                    <div
                        className="h-full w-full max-w-xl overflow-y-auto bg-white shadow-2xl"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-6 py-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Shift Details</p>
                                    <h3 className="mt-1 text-2xl font-bold text-slate-900">
                                        {auditShift ? format(parseISO(auditShift.startTime), 'EEE, d MMM yyyy') : 'Loading shift'}
                                    </h3>
                                </div>
                                <button
                                    onClick={closeShiftAudit}
                                    className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                                    title="Close"
                                >
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </div>
                        </div>

                        <div className="space-y-5 px-6 py-5">
                            {auditLoading && (
                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
                                    Loading shift history...
                                </div>
                            )}

                            {auditError && (
                                <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700">
                                    {auditError}
                                </div>
                            )}

                            {auditShift && (
                                <>
                                    <section className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <div className="mb-4 flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Current Assignment</p>
                                                <p className="mt-1 text-lg font-bold text-slate-900">{auditShift.user?.name || 'Unknown staff'}</p>
                                            </div>
                                            {auditMarker && (
                                                <span className={`rounded-full border px-3 py-1 text-xs font-bold ${auditMarker.className}`}>
                                                    {auditMarker.label}
                                                </span>
                                            )}
                                        </div>
                                        <dl className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Original assignee</dt>
                                                <dd className="mt-1 text-slate-900">{auditShift.originalUser?.name || auditShift.user?.name || 'Unknown'}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Time</dt>
                                                <dd className="mt-1 text-slate-900">{format(parseISO(auditShift.startTime), 'HH:mm')} - {format(parseISO(auditShift.endTime), 'HH:mm')}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Type</dt>
                                                <dd className="mt-1 text-slate-900">{auditShift.type}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Location</dt>
                                                <dd className="mt-1 text-slate-900">{auditShift.location || 'Not set'}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Change type</dt>
                                                <dd className="mt-1 text-slate-900">{auditShift.needsCover ? 'Needs cover' : auditShift.assignmentChangeType || (auditShift.assignmentChanged ? 'Manual' : 'None')}</dd>
                                            </div>
                                            <div>
                                                <dt className="font-bold uppercase tracking-wide text-slate-400">Source</dt>
                                                <dd className="mt-1 break-all text-slate-900">{auditShift.assignmentSourceSwapId || auditShift.coverSourceLeaveRequestId || 'None'}</dd>
                                            </div>
                                        </dl>
                                    </section>

                                    {auditShift.assignmentChangeType?.toUpperCase() === 'SWAP' && (
                                        <section className="rounded-2xl border border-indigo-200 bg-indigo-50/60 p-4">
                                            <div className="mb-4 flex items-center justify-between gap-3">
                                                <div>
                                                    <p className="text-xs font-bold uppercase tracking-wider text-indigo-500">Linked Swap</p>
                                                    <h4 className="mt-1 text-lg font-bold text-indigo-950">
                                                        {auditSwapPair
                                                            ? `${auditShift.originalUser?.name || 'Original staff'} swapped with ${auditSwapPair.originalUser?.name || auditSwapPair.user?.name || 'linked staff'}`
                                                            : 'Paired shift is not visible in this roster view'}
                                                    </h4>
                                                </div>
                                                {auditSwapPair && auditSwapPairInView && (
                                                    <button
                                                        onClick={() => scrollToShift(auditSwapPair.id)}
                                                        className="rounded-lg border border-indigo-200 bg-white px-3 py-2 text-xs font-bold text-indigo-700 hover:bg-indigo-100"
                                                    >
                                                        Jump to pair
                                                    </button>
                                                )}
                                            </div>
                                            <div className="grid gap-3 md:grid-cols-2">
                                                <div className="rounded-xl border border-indigo-100 bg-white p-3">
                                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">This shift now</p>
                                                    <p className="mt-1 font-bold text-slate-900">{auditShift.user?.name || 'Unknown staff'}</p>
                                                    <p className="mt-1 text-sm text-slate-600">
                                                        {format(parseISO(auditShift.startTime), 'EEE, d MMM')} · {format(parseISO(auditShift.startTime), 'HH:mm')} - {format(parseISO(auditShift.endTime), 'HH:mm')}
                                                    </p>
                                                </div>
                                                <div className="rounded-xl border border-indigo-100 bg-white p-3">
                                                    <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Corresponding shift</p>
                                                    {auditSwapPair ? (
                                                        <>
                                                            <p className="mt-1 font-bold text-slate-900">{auditSwapPair.user?.name || 'Unknown staff'}</p>
                                                            <p className="mt-1 text-sm text-slate-600">
                                                                {format(parseISO(auditSwapPair.startTime), 'EEE, d MMM')} · {format(parseISO(auditSwapPair.startTime), 'HH:mm')} - {format(parseISO(auditSwapPair.endTime), 'HH:mm')}
                                                            </p>
                                                        </>
                                                    ) : (
                                                        <p className="mt-1 text-sm text-slate-600">
                                                            Open the month containing the paired shift or use the audit source ID below.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <p className="mt-3 break-all text-xs text-indigo-700">
                                                Swap source: {auditShift.assignmentSourceSwapId || 'Not recorded'}
                                            </p>
                                        </section>
                                    )}

                                    <section className="rounded-2xl border border-slate-200 bg-white p-4">
                                        <div className="mb-4 flex items-center justify-between">
                                            <h4 className="text-sm font-bold uppercase tracking-wider text-slate-600">Audit Timeline</h4>
                                            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-600">
                                                {auditData?.auditLogs?.length || 0} logs
                                            </span>
                                        </div>

                                        {!auditData?.auditLogs?.length && !auditLoading ? (
                                            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">
                                                No audit records exist for this shift yet. Historical changes before this release may only show current metadata.
                                            </p>
                                        ) : (
                                            <div className="space-y-3">
                                                {auditData?.auditLogs?.map(log => {
                                                    const details = typeof log.details === 'object' && log.details ? log.details as Record<string, unknown> : {};
                                                    return (
                                                        <div key={log.id} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                                            <div className="flex items-start justify-between gap-3">
                                                                <div>
                                                                    <p className="font-bold text-slate-900">{getAuditActionLabel(log.action)}</p>
                                                                    <p className="mt-1 text-xs text-slate-500">
                                                                        {formatAuditDateTime(log.createdAt)} by {log.user?.name || 'System'}{log.user?.role?.name ? ` (${log.user.role.name})` : ''}
                                                                    </p>
                                                                </div>
                                                            </div>
                                                            <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-700">
                                                                {Boolean(details.beforeUserName || details.afterUserName) && (
                                                                    <p>
                                                                        <span className="font-bold text-slate-500">Assignment:</span>{' '}
                                                                        {String(details.beforeUserName || details.beforeUserId || 'Unknown')} to {String(details.afterUserName || details.afterUserId || 'Unknown')}
                                                                    </p>
                                                                )}
                                                                {Boolean(details.leaveType) && (
                                                                    <p><span className="font-bold text-slate-500">Leave type:</span> {String(details.leaveType)}</p>
                                                                )}
                                                                {Boolean(details.adminNotes) && (
                                                                    <p><span className="font-bold text-slate-500">Admin notes:</span> {String(details.adminNotes)}</p>
                                                                )}
                                                                {Boolean(details.swapId || details.leaveRequestId) && (
                                                                    <p className="break-all text-xs text-slate-500">
                                                                        Source ID: {String(details.swapId || details.leaveRequestId)}
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </section>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={showBulkDeleteConfirm}
                title="Delete Selected Shifts"
                message={`Delete ${selectedShiftIds.length} selected shift(s)?`}
                confirmLabel="Delete"
                variant="danger"
                onConfirm={handleBulkDelete}
                onCancel={() => setShowBulkDeleteConfirm(false)}
            />
        </>
    );
};

export default MonthlyRosterView;
